import { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { JsonEditor, ThemeInput } from "json-edit-react";
import { GenericObject } from "../services/Price";
import { ConfigurationModes, Service } from "./interfaces";

// ETL connectors have no mapping/meta tabs, so those modes fall back to PARAMS.
export const ETL_UNSUPPORTED_MODES = new Set<ConfigurationModes>([
  ConfigurationModes.UH_CATS,
  ConfigurationModes.META,
]);

// The mode a service actually renders: an ETL service can't show UH_CATS/META,
// so it falls back to PARAMS; every other service renders the requested mode.
export const getEffectiveMode = (
  service: Service,
  currentMode: ConfigurationModes
): ConfigurationModes =>
  service.schema === "etl" && ETL_UNSUPPORTED_MODES.has(currentMode)
    ? ConfigurationModes.PARAMS
    : currentMode;

type JSONMode = "read_only" | "editable";

export const BeautifulJSON = ({
  data,
  onDataChanged,
  mode,
}: {
  data: GenericObject;
  onDataChanged: (newData: GenericObject | unknown) => void;
  mode: JSONMode;
}) => {
  const muiTheme = useTheme();

  const jsonTheme = useMemo<ThemeInput>(
    () => ({
      styles: {
        container: {
          backgroundColor: muiTheme.vars.palette.background.paper,
          color: muiTheme.vars.palette.text.primary,
        },
        property: { color: muiTheme.vars.palette.primary.main },
        bracket: { color: muiTheme.vars.palette.text.primary },
        itemCount: {
          color: muiTheme.vars.palette.text.secondary,
          fontStyle: "italic",
        },
        string: { color: muiTheme.vars.palette.success.main },
        number: { color: muiTheme.vars.palette.warning.main },
        boolean: { color: muiTheme.vars.palette.info.main },
        null: { color: muiTheme.vars.palette.text.secondary },
        input: {
          backgroundColor: muiTheme.vars.palette.background.default,
          color: muiTheme.vars.palette.text.primary,
        },
        iconCollection: { color: muiTheme.vars.palette.text.secondary },
        iconEdit: { color: muiTheme.vars.palette.text.secondary },
        iconDelete: { color: muiTheme.vars.palette.error.main },
        iconAdd: { color: muiTheme.vars.palette.success.main },
        iconOk: { color: muiTheme.vars.palette.success.main },
        iconCancel: { color: muiTheme.vars.palette.error.main },
      },
    }),
    [muiTheme]
  );

  return (
    <JsonEditor
      data={data}
      setData={onDataChanged}
      maxWidth={"100%"}
      theme={jsonTheme}
      restrictAdd={mode === "read_only"}
      restrictEdit={mode === "read_only"}
      restrictDelete={mode === "read_only"}
      rootName=""
    />
  );
};
